import { useMutation, useQueryClient } from "react-query";

import { deleteTarget } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";

interface DeleteTargetPayload {
  targetId: number;
  /** Parent goal id — required for invalidating the target list query. */
  goalId: number;
}

/**
 * Permanently delete a target and all its session data. Surfaced behind the
 * `DeleteTargetDialog` confirmation per product spec — never call this hook
 * without explicit user confirmation.
 *
 * Invalidates target list + detail queries on success.
 */
const useDeleteTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: deleteTargetAction,
    isLoading: isDeletingTarget,
    ...rest
  } = useMutation(({ targetId }: DeleteTargetPayload) => deleteTarget(targetId), {
    onSuccess: (_response, variables) => {
      showToast({ message: "Target deleted" });
      queryClient.invalidateQueries([GET_TARGET_BY_ID, variables.targetId]);
      queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
      queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
      onSuccess(_response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to delete target";
      showToast({ message: errorMessage, errorState: true });
      onError(error);
    },
    ...otherOptions,
  });

  return { deleteTargetAction, isDeletingTarget, ...rest };
};

export default useDeleteTarget;
